import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Scissors, Heart, MapPin, Clock, 
  Phone, CreditCard, Compass, Share2, 
  Camera, Star, User, ShieldCheck, 
  ChevronRight, Check, X, Sparkles, 
  Search, Info, Image as ImageIcon
} from "lucide-react";
import { useSalon, useSalonServices, useSalonStylists, useSalonReviews, useToggleFavourite, useToast } from "../api/hooks";
import { useAuth } from "../App";
import { useBreakpoint } from "../hooks/useBreakpoint";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Bokeh  from "../components/Bokeh";
import { Stars, Toast, BtnGold, BtnGhost, Badge, Spinner } from "../components/UI";
import { T, GLOBAL_STYLES } from "../tokens";

const SERVICE_CATS = ["all","hair","skin","bridal","nails","men","grooming"];
const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const LUXURY_PHOTOS = [
  "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1562322140-8baeececf3df?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1605497746444-ac9da58d4400?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1519699047748-de8e457a634e?q=80&w=1200&auto=format&fit=crop"
];

export default function SalonDetail() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { msg, visible, show } = useToast();
  const { isMobile, isTablet } = useBreakpoint();

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  const { data: salonData,    isLoading } = useSalon(slug);
  const salon = salonData?.data?.data || salonData?.data;

  const { data: servicesData } = useSalonServices(salon?.id, {});
  const { data: stylistsData } = useSalonStylists(salon?.id);
  const { data: reviewsData  } = useSalonReviews(salon?.id, {});
  const { mutate: toggleFav  } = useToggleFavourite();

  const services = servicesData?.data?.data || servicesData?.data || salon?.services || [];
  const stylists  = stylistsData?.data?.data || stylistsData?.data || salon?.stylists || [];
  const reviews   = reviewsData?.data?.data  || reviewsData?.data  || salon?.reviews  || [];

  const [cart,          setCart]          = useState([]);
  const [selStylist,    setSelStylist]    = useState(null);
  const [activeCat,     setActiveCat]     = useState("all");
  const [saved,         setSaved]         = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [barsVisible,   setBarsVisible]   = useState(false);
  const barsRef = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setBarsVisible(true); }, { threshold:.3 });
    if (barsRef.current) io.observe(barsRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const ids = ["overview","services","stylists","reviews","location"];
    const h = () => {
      let cur = "overview";
      ids.forEach(id => { const el = document.getElementById(`sec-${id}`); if (el && el.getBoundingClientRect().top <= 140) cur = id; });
      setActiveSection(cur);
    };
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const scrollTo = (id) => document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior:"smooth" });
  const toggleSvc = (svc) => setCart(prev => prev.find(c=>c.id===svc.id) ? prev.filter(c=>c.id!==svc.id) : [...prev, svc]);
  const cartTotal  = cart.reduce((a,s) => a + Number(s.price), 0);

  const filtered = activeCat === "all" ? services : services.filter(s => s.category?.slug === activeCat || s.category?.name?.toLowerCase() === activeCat);

  const ratingBreakdown = [
    { star:5, pct: reviews.length ? Math.round(reviews.filter(r=>r.rating===5).length/reviews.length*100) : 75 },
    { star:4, pct: reviews.length ? Math.round(reviews.filter(r=>r.rating===4).length/reviews.length*100) : 14 },
    { star:3, pct: 6 }, { star:2, pct: 3 }, { star:1, pct: 2 },
  ];

  if (isLoading) return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ minHeight:"70vh", display:"flex", alignItems:"center", justifyContent:"center" }}><Spinner size={40}/></div>
    </>
  );

  if (!salon) return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ minHeight:"60vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
        <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:26, color:T.text }}>Salon not found</div>
        <BtnGold onClick={() => navigate("/salons")}>Browse Salons</BtnGold>
      </div>
      <Footer/>
    </>
  );

  return (
    <>
      <style>{GLOBAL_STYLES}</style>

      {/* Gallery */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1.8fr 1fr", gridTemplateRows: isMobile ? "240px" : "280px 180px", gap:3, height: isMobile ? 240 : 464, background: T.dark4 }}>
        {[0,1,2].map(i => {
          const gallery = salon?.images?.length ? salon.images : LUXURY_PHOTOS;
          const imgUrl = gallery[i % gallery.length];
          return (
            <div key={i} style={{ position:"relative", overflow:"hidden", ...(i===0?{gridRow:"1/3"}:{}) }}>
              <img 
                src={imgUrl} 
                alt={`${salon?.name || "Salon"} ${i+1}`} 
                style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                onMouseOver={e => e.currentTarget.style.transform = "scale(1.05)"}
                onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
              />
              <div style={{ position:"absolute", inset:0, background: "linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5))" }} />
              {i===0 && (
                <button onClick={() => setGalleryOpen(true)} style={{ position:"absolute", bottom:12, right:12, background:"rgba(13,11,8,.82)", backdropFilter:"blur(6px)", border:`1px solid ${T.goldBdrS}`, padding:"8px 16px", borderRadius:8, fontSize:12.5, color:T.goldLt, cursor:"pointer", fontFamily:"inherit", zIndex:2, display: "flex", alignItems: "center", gap: 8 }}>
                  <Camera size={16} /> View all photos
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Sticky nav */}
      <div style={{ position:"sticky", top:72, zIndex:100, background:"rgba(13,11,8,.96)", backdropFilter:"blur(12px)", borderBottom:`1px solid ${T.goldBdr}`, padding:"0 48px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", fontFamily:"'DM Sans',sans-serif" }}>
        <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:20, fontWeight:600, color:T.text, marginRight:16, flexShrink:0 }}>{salon.name}</span>
        <div style={{ display:"flex", flex:1 }}>
          {["overview","services","stylists","reviews","location"].map(id => (
            <button key={id} onClick={() => scrollTo(id)} style={{ padding:"0 16px", height:56, background:"transparent", border:"none", borderBottom:`2px solid ${activeSection===id?T.gold:"transparent"}`, color:activeSection===id?T.goldLt:T.muted, fontSize:13.5, cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize", transition:"all .2s" }}>{id}</button>
          ))}
        </div>
        <BtnGold size="sm" onClick={() => scrollTo("bookWidget")}>Book Now</BtnGold>
      </div>

      {/* Content */}
      <div style={{ display:"grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 340px", gap: isMobile ? 20 : 30, padding: isMobile ? "24px 16px" : "38px 48px", maxWidth:1280, margin:"0 auto" }}>
        {/* Left */}
        <div>
          {/* Overview */}
          <div id="sec-overview" style={{ marginBottom:42, scrollMarginTop:130 }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:40, fontWeight:600, color:T.text, lineHeight:1.1, marginBottom:8 }}>{salon.name}</div>
            <div style={{ fontSize:13, color:T.goldLt, letterSpacing:".1em", textTransform:"uppercase", marginBottom:14 }}>{salon.tagline||`${salon.area}, ${salon.city}`}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:9, marginBottom:16 }}>
              {[
                { icon: <MapPin size={14} />, text: salon.address },
                { icon: <Clock size={14} />, text: salon.status === "approved" ? "Open Now" : "Closed" },
                { icon: <Phone size={14} />, text: salon.phone },
                { icon: <CreditCard size={14} />, text: (salon.payment_methods || []).join(" · ") || "UPI · Cards · Cash" }
              ].map((item, i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, background:T.dark3, border:`1px solid ${T.goldBdr}`, padding:"6px 14px", borderRadius:20, fontSize:12.5 }}>
                  <span style={{ color: T.gold }}>{item.icon}</span><span style={{ color:T.muted }}>{item.text}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:22, marginBottom:18 }}>
              <div>
                <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:36, fontWeight:600, color:T.goldLt, lineHeight:1 }}>{salon.rating}</div>
                <Stars rating={salon.rating} size={17}/>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{salon.review_count?.toLocaleString("en-IN")} reviews</div>
              </div>
              {[{v:`${stylists.length||"12"}+`,l:"Stylists"},{v:`${services.length||"50"}+`,l:"Services"}].map(s=>(
                <div key={s.l} style={{ textAlign:"center" }}>
                  <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:26, fontWeight:600, color:T.goldLt }}>{s.v}</div>
                  <div style={{ fontSize:11.5, color:T.muted }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[
                { icon: <Phone size={14} />, label: "Call", fn: () => { if (salon.phone) window.location.href = `tel:${salon.phone}`; else show("No phone number available"); } },
                { icon: <Compass size={14} />, label: "Directions", fn: () => { if (salon.address) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(salon.address + ' ' + (salon.city || ''))}`, '_blank'); else show("No address available"); } },
                { icon: <Share2 size={14} />, label: "Share", fn: () => { navigator.clipboard.writeText(window.location.href).then(() => show("Link copied! ✓")).catch(() => show("Could not copy link")); } }
              ].map((item)=>(
                <button key={item.label} onClick={item.fn} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px", borderRadius:8, border:`1px solid ${T.goldBdr}`, background:T.dark3, color:T.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }} onMouseOver={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.background=T.goldDim;e.currentTarget.style.color=T.text;}} onMouseOut={e=>{e.currentTarget.style.borderColor=T.goldBdr;e.currentTarget.style.background=T.dark3;e.currentTarget.style.color=T.muted;}}>
                  <span style={{ color: T.gold }}>{item.icon}</span>{item.label}
                </button>
              ))}
              <button onClick={()=>{setSaved(v=>!v);toggleFav(salon.id);show(saved?"Removed from saved":"Saved");}} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px", borderRadius:8, border:`1px solid ${saved?T.gold:T.goldBdr}`, background:saved?T.goldDim:T.dark3, color:saved?T.goldLt:T.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
                <Heart size={14} fill={saved ? T.gold : "transparent"} color={saved ? T.gold : "currentColor"} />
                {saved?"Saved":"Save"}
              </button>
            </div>
          </div>

          {/* Services */}
          <div id="sec-services" style={{ marginBottom:42, scrollMarginTop:130 }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.text, marginBottom:14, paddingBottom:10, borderBottom:`1px solid ${T.goldBdr}` }}>Services & Pricing</div>
            <div style={{ display:"flex", gap:7, marginBottom:14, flexWrap:"wrap" }}>
              {SERVICE_CATS.map(c=>(
                <button key={c} onClick={()=>setActiveCat(c)} style={{ padding:"5px 15px", borderRadius:20, border:`1px solid ${activeCat===c?T.gold:T.goldBdr}`, background:activeCat===c?T.goldDim:"transparent", color:activeCat===c?T.goldLt:T.muted, fontSize:12.5, cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize" }}>{c==="all"?"All":c}</button>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
              {filtered.map(svc => {
                const inCart = !!cart.find(c=>c.id===svc.id);
                return (
                  <div key={svc.id} onClick={()=>toggleSvc(svc)} style={{ display:"flex", alignItems:"center", gap:14, padding:14, background:inCart?T.goldDim:T.dark3, border:`1px solid ${inCart?T.gold:T.goldBdr}`, borderRadius:12, cursor:"pointer", transition:"all .22s" }}>
                    <div style={{ width:44, height:44, borderRadius:10, background:inCart?"rgba(201,168,76,.2)":T.dark4, border:`1px solid ${inCart?T.gold:T.goldBdr}`, display:"flex", alignItems:"center", justifyContent:"center", color: inCart ? T.gold : T.muted, flexShrink:0 }}>
                      <Scissors size={20} />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:500, color:inCart?T.goldLt:T.text }}>{svc.name}</div>
                      <div style={{ fontSize:12, color:T.muted, display: "flex", alignItems: "center", gap: 6 }}>
                        {svc.description} · <Clock size={12} /> {svc.duration_mins} min
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:19, fontWeight:600, color:T.goldLt }}>₹{Number(svc.price).toLocaleString("en-IN")}</div>
                      <button onClick={e=>{e.stopPropagation();toggleSvc(svc);}} style={{ marginTop:4, padding:"4px 12px", borderRadius:7, fontSize:11.5, fontWeight:600, cursor:"pointer", fontFamily:"inherit", background:inCart?`linear-gradient(135deg,${T.goldLt},${T.gold})`:"transparent", border:`1px solid ${inCart?T.gold:T.goldBdr}`, color:inCart?T.dark:T.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {inCart ? <><Check size={12} /> Added</> : "+ Add"}
                      </button>
                    </div>
                  </div>
                );
              })}
              {filtered.length===0 && services.length===0 && <div style={{ padding:"24px", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:12, textAlign:"center", color:T.muted }}>Loading services...</div>}
            </div>
          </div>

          {/* Stylists */}
          <div id="sec-stylists" style={{ marginBottom:42, scrollMarginTop:130 }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.text, marginBottom:16, paddingBottom:10, borderBottom:`1px solid ${T.goldBdr}` }}>Our Stylists</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))", gap:14 }}>
              {stylists.map(s => {
                const sel = selStylist===s.id;
                return (
                  <div key={s.id} onClick={()=>{setSelStylist(v=>v===s.id?null:s.id);show(sel?`${s.name} deselected`:`${s.name} selected`);}} style={{ background:sel?T.goldDim:T.dark3, border:`1px solid ${sel?T.gold:T.goldBdr}`, borderRadius:14, padding:"18px 14px", textAlign:"center", cursor:"pointer", transition:"all .25s" }}>
                    <div style={{ width:62, height:62, borderRadius:"50%", background:`linear-gradient(135deg,${T.goldDim},${T.dark4})`, border:`2px solid ${sel?T.gold:T.goldBdrS}`, display:"flex", alignItems:"center", justifyContent:"center", color: T.gold, margin:"0 auto 10px", position:"relative" }}>
                      <User size={28} />
                      <div style={{ position:"absolute", bottom:2, right:2, width:12, height:12, borderRadius:"50%", background:s.is_available?T.green:T.red, border:`2px solid ${T.dark3}` }}/>
                    </div>
                    <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:15, fontWeight:600, color:T.text, marginBottom:3 }}>{s.name}</div>
                    <div style={{ fontSize:11.5, color:T.muted, marginBottom:6 }}>{s.specialization}</div>
                    <Stars rating={s.rating||0} size={11}/>
                    <div style={{ fontSize:11, color:T.subtle, marginTop:3 }}>{s.experience_years}yr exp</div>
                  </div>
                );
              })}
              {stylists.length===0 && Array.from({length:3}).map((_,i)=>(
                <div key={i} style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:14, padding:"18px 14px", textAlign:"center" }}>
                  <div style={{ width:62, height:62, borderRadius:"50%", background:T.dark4, margin:"0 auto 10px" }}/>
                  <div style={{ height:12, background:T.dark4, borderRadius:4, marginBottom:6 }}/>
                  <div style={{ height:10, background:T.dark4, borderRadius:4, width:"70%", margin:"0 auto" }}/>
                </div>
              ))}
            </div>
          </div>

          {/* Reviews */}
          <div id="sec-reviews" style={{ marginBottom:42, scrollMarginTop:130 }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.text, marginBottom:14, paddingBottom:10, borderBottom:`1px solid ${T.goldBdr}` }}>Guest Reviews</div>
            <div ref={barsRef} style={{ display:"flex", gap:24, alignItems:"center", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:14, padding:"20px 22px", marginBottom:18 }}>
              <div style={{ textAlign:"center", flexShrink:0, minWidth:80 }}>
                <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:50, fontWeight:600, color:T.goldLt, lineHeight:1 }}>{salon.rating}</div>
                <Stars rating={salon.rating} size={15}/>
                <div style={{ fontSize:12, color:T.muted, marginTop:4 }}>{salon.review_count?.toLocaleString("en-IN")} reviews</div>
              </div>
              <div style={{ flex:1 }}>
                {ratingBreakdown.map(r=>(
                  <div key={r.star} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                    <span style={{ fontSize:12, color:T.muted, width:12, textAlign:"right", flexShrink:0 }}>{r.star}</span>
                    <div style={{ flex:1, height:6, background:T.dark4, borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:3, background:`linear-gradient(90deg,${T.goldLt},${T.gold})`, width:barsVisible?`${r.pct}%`:"0%", transition:"width 1.2s ease" }}/>
                    </div>
                    <span style={{ fontSize:11, color:T.muted, width:32, textAlign:"right" }}>{r.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {reviews.slice(0,4).map(r=>(
                <div key={r.id} style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:12, padding:"16px 18px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                    <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${T.goldDim},${T.dark4})`, border:`1.5px solid ${T.goldBdr}`, display:"flex", alignItems:"center", justifyContent:"center", color: T.goldLt }}>
                      <User size={18} />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13.5, fontWeight:500, color:T.text }}>{r.user?.name||"Customer"}</div>
                      <Stars rating={r.rating} size={11}/>
                    </div>
                    <div style={{ fontSize:11.5, color:T.muted }}>{new Date(r.created_at).toLocaleDateString("en-IN",{month:"short",year:"numeric"})}</div>
                  </div>
                  <p style={{ fontSize:13.5, color:T.muted, lineHeight:1.75 }}>{r.comment}</p>
                </div>
              ))}
              {reviews.length===0 && <div style={{ padding:"24px", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:12, textAlign:"center", color:T.muted }}>No reviews yet. Be the first!</div>}
            </div>
          </div>

          {/* Location */}
          <div id="sec-location" style={{ marginBottom:42, scrollMarginTop:130 }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.text, marginBottom:14, paddingBottom:10, borderBottom:`1px solid ${T.goldBdr}` }}>Location & Hours</div>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:18 }}>
              <div style={{ height:230, borderRadius:14, overflow:"hidden", border:`1px solid ${T.goldBdr}`, position:"relative", background:"linear-gradient(135deg,#0F1A10,#0A120A)" }}>
                <div style={{ position:"absolute", inset:0, opacity:.25, backgroundImage:`linear-gradient(rgba(201,168,76,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,.5) 1px,transparent 1px)`, backgroundSize:"32px 32px" }}/>
                <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:14, height:14, borderRadius:"50%", background:T.gold, boxShadow:`0 0 0 4px rgba(201,168,76,.25),0 0 0 8px rgba(201,168,76,.1)`, animation:"fyzPulse 2.5s infinite" }}/>
                <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-52px)", background:"rgba(13,11,8,.9)", border:`1px solid ${T.goldBdrS}`, padding:"5px 12px", borderRadius:8, fontSize:12, color:T.goldLt, whiteSpace:"nowrap" }}>{salon.name}</div>
                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(0deg,rgba(13,11,8,.9),transparent)", padding:"12px 14px" }}>
                  <div style={{ fontSize:12, color:T.muted, display: "flex", alignItems: "center", gap: 6 }}><MapPin size={12} /> {salon.address}</div>
                </div>
              </div>
              <div style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:14, padding:16 }}>
                <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:12, display: "flex", alignItems: "center", gap: 6 }}>
                  <Clock size={12} /> Working Hours
                </div>
                {(salon.working_hours||DAYS.map(d=>({day:d,open:"09:00",close:"21:00"}))).map((h,i)=>(
                  <div key={h.day} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid rgba(201,168,76,.06)`, fontSize:13 }}>
                    <span style={{ color:T.muted }}>{h.day}</span>
                    <span style={{ color:T.text }}>{h.open||"09:00"} – {h.close||"21:00"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display:"flex", flexDirection:"column", gap:18, position:"sticky", top:136, alignSelf:"start" }}>
          {/* Booking widget */}
          <div id="sec-bookWidget" style={{ background:"linear-gradient(160deg,#2A200E,#1A1510)", border:`1.5px solid ${T.goldBdrS}`, borderRadius:16, padding:22, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-40, right:-40, width:160, height:160, borderRadius:"50%", background:"radial-gradient(circle,rgba(201,168,76,.1) 0%,transparent 70%)", pointerEvents:"none" }}/>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:18, fontWeight:600, color:T.text, marginBottom:3 }}>Book Your Visit</div>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:30, fontWeight:600, color:T.goldLt, marginBottom:3 }}>{cart.length>0?`₹${cartTotal.toLocaleString("en-IN")}`:"₹0"}</div>
            <div style={{ fontSize:12, color:T.muted, marginBottom:cart.length?14:18 }}>{cart.length>0?`${cart.length} service${cart.length>1?"s":""} selected`:"Select services to proceed"}</div>
            {cart.length>0&&(
              <>
                {cart.map(s=>(
                  <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid rgba(201,168,76,.08)`, fontSize:13 }}>
                    <span style={{ color:T.text, display: "flex", alignItems: "center", gap: 6 }}><Scissors size={14} color={T.gold} /> {s.name}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ color:T.goldLt }}>₹{Number(s.price).toLocaleString("en-IN")}</span>
                      <X onClick={()=>toggleSvc(s)} size={16} style={{ color:T.subtle, cursor:"pointer" }} />
                    </div>
                  </div>
                ))}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems: "center", padding:"12px 0 16px" }}>
                  <span style={{ fontSize:13, color:T.muted }}>Total</span>
                  <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.goldLt }}>₹{cartTotal.toLocaleString("en-IN")}</span>
                </div>
              </>
            )}
            <BtnGold full disabled={cart.length===0} onClick={()=>navigate("/book",{state:{salon,cart,stylist:selStylist?stylists.find(s=>s.id===selStylist):null}})} style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
              {cart.length>0 ? <>Book Appointment <ChevronRight size={18} /></> : "Select Services to Book"}
            </BtnGold>
            <p style={{ textAlign:"center", fontSize:11.5, color:T.muted, marginTop:10, display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
              <Info size={12} /> Free cancellation up to 2 hours before
            </p>
          </div>
          {/* Amenities */}
          <div style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:14, padding:16 }}>
            <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:12 }}>Amenities</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {(salon.amenities||["Free Parking","AC Inside","Free Wi-Fi","Premium Products"]).map(a=>(
                <div key={a} style={{ fontSize:12.5, color:T.muted, display: "flex", alignItems: "center", gap: 6 }}>
                  <Check size={12} color={T.green} /> {a}
                </div>
              ))}
            </div>
          </div>
          {/* Gold promo */}
          <div style={{ background:"linear-gradient(135deg,#2A200E,#1C1510)", border:`1.5px solid ${T.goldBdrS}`, borderRadius:14, padding:20, textAlign:"center" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(201,168,76,0.1)", border: `1px solid ${T.gold}`, display: "flex", alignItems: "center", justifyContent: "center", color: T.gold }}>
                <ShieldCheck size={24} />
              </div>
            </div>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:16, fontWeight: 600, color:T.text, marginBottom:6 }}>Save <span style={{ color:T.goldLt }}>20%</span> with Gold</div>
            <p style={{ fontSize:12, color:T.muted, marginBottom:16, lineHeight:1.6 }}>Exclusive discounts on all services</p>
            <BtnGold full size="sm" onClick={()=>navigate("/membership")} style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
              Get Gold <ChevronRight size={16} />
            </BtnGold>
          </div>
        </div>
      </div>

      <Footer/>

      {/* Full-Screen Photo Gallery Lightbox */}
      {galleryOpen && (
        <div 
          onClick={() => setGalleryOpen(false)}
          style={{ 
            position: "fixed", inset: 0, zIndex: 1100, 
            background: "rgba(10, 8, 6, 0.97)", backdropFilter: "blur(12px)", 
            display: "flex", flexDirection: "column", alignItems: "center", 
            justifyContent: "center", padding: isMobile ? 12 : 40 
          }}
        >
          {/* Close Button */}
          <button 
            type="button"
            onClick={() => setGalleryOpen(false)}
            style={{ 
              position: "absolute", top: isMobile ? 16 : 24, right: isMobile ? 16 : 24, 
              width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.05)", 
              border: `1.5px solid ${T.goldBdrS}`, color: T.gold, display: "flex", 
              alignItems: "center", justifyContent: "center", cursor: "pointer", 
              transition: "all 0.2s", zIndex: 1200 
            }}
            onMouseOver={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "scale(1.05)"; }}
            onMouseOut={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.transform = "scale(1)"; }}
          >
            <X size={20} />
          </button>

          {/* Main Image Slider Area */}
          <div 
            onClick={e => e.stopPropagation()}
            style={{ 
              position: "relative", width: "100%", maxWidth: 960, 
              height: isMobile ? "60vh" : "66vh", display: "flex", 
              alignItems: "center", justifyContent: "center", marginBottom: 20 
            }}
          >
            {/* Left Navigate Arrow */}
            <button 
              type="button"
              onClick={() => {
                const gallery = salon?.images?.length ? salon.images : LUXURY_PHOTOS;
                setActivePhotoIndex(prev => (prev - 1 + gallery.length) % gallery.length);
              }}
              style={{ 
                position: "absolute", left: isMobile ? 8 : -24, 
                width: 48, height: 48, borderRadius: "50%", background: "rgba(13,11,8,0.85)", 
                border: `1px solid ${T.goldBdrS}`, color: T.goldLt, display: "flex", 
                alignItems: "center", justifyContent: "center", cursor: "pointer", 
                transition: "all 0.2s", zIndex: 1200 
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = T.gold}
              onMouseOut={e => e.currentTarget.style.borderColor = T.goldBdrS}
            >
              <ChevronRight size={22} style={{ transform: "rotate(180deg)" }} />
            </button>

            {/* Active Image */}
            <img 
              src={(salon?.images?.length ? salon.images : LUXURY_PHOTOS)[activePhotoIndex]} 
              alt={`${salon?.name || "Salon"} Full view ${activePhotoIndex + 1}`}
              style={{ 
                maxWidth: "100%", maxHeight: "100%", objectFit: "contain", 
                borderRadius: 16, border: `1.5px solid ${T.goldBdr}`, 
                boxShadow: "0 24px 64px rgba(0,0,0,0.9)" 
              }} 
            />

            {/* Right Navigate Arrow */}
            <button 
              type="button"
              onClick={() => {
                const gallery = salon?.images?.length ? salon.images : LUXURY_PHOTOS;
                setActivePhotoIndex(prev => (prev + 1) % gallery.length);
              }}
              style={{ 
                position: "absolute", right: isMobile ? 8 : -24, 
                width: 48, height: 48, borderRadius: "50%", background: "rgba(13,11,8,0.85)", 
                border: `1px solid ${T.goldBdrS}`, color: T.goldLt, display: "flex", 
                alignItems: "center", justifyContent: "center", cursor: "pointer", 
                transition: "all 0.2s", zIndex: 1200 
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = T.gold}
              onMouseOut={e => e.currentTarget.style.borderColor = T.goldBdrS}
            >
              <ChevronRight size={22} />
            </button>
          </div>

          {/* Thumbnails Row */}
          <div 
            onClick={e => e.stopPropagation()}
            style={{ 
              display: "flex", gap: 10, overflowX: "auto", maxWidth: "100%", 
              padding: "4px 16px", scrollbarWidth: "none" 
            }}
          >
            {(salon?.images?.length ? salon.images : LUXURY_PHOTOS).map((img, index) => {
              const active = index === activePhotoIndex;
              return (
                <div 
                  key={index}
                  onClick={() => setActivePhotoIndex(index)}
                  style={{ 
                    width: 72, height: 52, borderRadius: 8, overflow: "hidden", 
                    border: `2px solid ${active ? T.gold : "transparent"}`, 
                    cursor: "pointer", opacity: active ? 1 : 0.5, 
                    transition: "all 0.2s", flexShrink: 0 
                  }}
                >
                  <img src={img} alt="Thumbnail" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Toast msg={msg} visible={visible}/>
    </>
  );
}

